import { useMemo } from "react";
import { trpc } from "@/lib/trpc";

/**
 * Origens de uma oportunidade (`deals.sourceId`).
 *
 * Antes era uma lista fixa aqui, espelhando o enum `dealSource` do banco.
 * Acrescentar "Instagram" exigia migration e deploy, ou seja, o administrador
 * não tinha como mexer — agora vêm de `deal_sources`, gerenciadas na tela de
 * configurações do CRM.
 *
 * As oportunidades trazem só o `sourceId`; o nome sai deste mapa. Buscar a
 * lista uma vez e resolver no cliente evita um join em toda listagem de
 * oportunidade por causa de um rótulo.
 */
export type DealSourceOption = { id: number; name: string; archived: boolean };

export function useDealSources() {
  // Origens mudam raramente e a lista é pequena: vale segurar por alguns
  // minutos em vez de refazer a query a cada tela que precisa do rótulo.
  const query = trpc.crm.sources.list.useQuery(
    { includeArchived: true },
    { staleTime: 5 * 60_000 },
  );

  const all = (query.data ?? []) as DealSourceOption[];

  return useMemo(() => {
    // Arquivadas continuam no mapa de rótulos — uma oportunidade antiga não
    // pode passar a mostrar "Origem #7" porque o canal foi desativado — mas
    // ficam fora das opções de escolha.
    const byId = new Map(all.map((s) => [s.id, s.name]));
    return {
      options: all.filter((s) => !s.archived),
      all,
      label: (id: number | null | undefined) =>
        id == null ? null : byId.get(id) ?? null,
      isLoading: query.isLoading,
    };
  }, [all, query.isLoading]);
}
